import { Link } from 'react-router-dom';
import { PRODUCTS, accentBg } from '../data/products';
import { useCurrency } from '../context/CurrencyContext';
import { ProductCard } from '../components/ProductCard';
import { CountdownBanner } from '../components/CountdownBanner';
import { ChevronRight, Sparkles, Leaf, Heart } from 'lucide-react';

const testimonials = [
  { quote: "After two weeks my skin looked like I'd slept eight hours every night. The Collagen mask is unreal.", author: 'Amara R.', age: 34 },
  { quote: 'I’ve tried every serum on earth. Nothing made me glow like the Vitamin C ritual on a Sunday night.', author: 'Mira L.', age: 27 },
  { quote: 'My mother is 72 and her cheeks are bouncy again. We share the box now.', author: 'Sophia D.', age: 41 },
];

export default function HomePage() {
  const { format } = useCurrency();
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center pt-12 md:pt-24 pb-20 md:pb-32">
          <div className="md:col-span-5 order-2 md:order-1 animate-fade-up">
            <p className="overline mb-6">✨ Glow Starts Here</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Luxury skin <em className="not-italic text-lum-sale">transformation</em> in minutes.
            </h1>
            <p className="mt-6 text-lum-text2 max-w-md text-base leading-relaxed">
              Reveal your best skin yet with our advanced Neutriherbs Sheet Mask Collection — designed to deeply hydrate, smooth, and brighten your complexion from the very first use.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-button" className="btn-primary">Shop the Ritual</Link>
              <Link to="/quiz" data-testid="hero-quiz-button" className="btn-secondary">Find your mask</Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.22em] text-lum-text2">
              <span className="flex items-center gap-2"><Sparkles size={14} /> Free gift</span>
              <span className="flex items-center gap-2"><Heart size={14} /> Cruelty free</span>
              <span className="flex items-center gap-2"><Leaf size={14} /> Vegan</span>
            </div>
          </div>
          <div className="md:col-span-7 order-1 md:order-2 relative">
            <div className="aspect-[4/5] bg-lum-bg2 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1555820585-c5ae44394b79?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Glowing skin model"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-10 bg-lum-bg px-6 py-5 shadow-md border border-lum-border max-w-[240px]">
              <p className="font-serif-display text-3xl leading-none">12,400+</p>
              <p className="text-xs uppercase tracking-[0.2em] text-lum-text2 mt-2">Five-star rituals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust marquee */}
      <section className="border-y border-lum-border bg-lum-bg2/60 overflow-hidden">
        <div className="flex gap-16 py-5 animate-marquee whitespace-nowrap text-xs uppercase tracking-[0.32em] text-lum-text2">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-16">
              <span>★ Free gift, every order</span>
              <span>✦ Dermatologist tested</span>
              <span>★ Free shipping over $24</span>
              <span>✦ 4D Hyaluronic Acid</span>
              <span>★ Buy 2 packs, save 10%</span>
              <span>✦ Vegan & cruelty free</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bestsellers — Choose Your Skin Solution */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline mb-3">💎 Choose Your Skin Solution</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Targeted treatments,<br/>spa-level results.</h2>
          </div>
          <Link to="/shop" data-testid="bestsellers-shopall" className="hidden md:inline-flex items-center gap-2 btn-ghost">Shop all <ChevronRight size={14} /></Link>
        </div>
        <p className="text-lum-text2 max-w-2xl text-base leading-relaxed mb-16">
          Each mask is powered by high-performance skincare actives that work instantly and visibly — because your skin deserves results you can see and feel. Whether you want to plump fine lines, restore hydration, or unlock a radiant glow.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 lg:gap-16 items-stretch">
          {PRODUCTS.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>

        {/* Product narrative blocks */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          <div className="border-t border-lum-border pt-8">
            <p className="overline text-lum-sale mb-3">💙 Collagen Smoothing Mask</p>
            <p className="font-serif-display text-2xl md:text-3xl leading-tight mb-4">Firm. Lift. Restore.</p>
            <p className="text-sm text-lum-text2 leading-relaxed">
              Powered by collagen + peptides, this mask helps improve elasticity, reduce the appearance of fine lines, and leave skin looking plump, bouncy, and youthful.
            </p>
          </div>
          <div className="border-t border-lum-border pt-8">
            <p className="overline text-lum-sale mb-3">💧 Hyaluronic Acid Mask</p>
            <p className="font-serif-display text-2xl md:text-3xl leading-tight mb-4">Hydration that goes deeper.</p>
            <p className="text-sm text-lum-text2 leading-relaxed">
              With 4D hyaluronic acid technology, this ultra-hydrating formula penetrates multiple layers of skin to lock in moisture, repair dryness, and deliver long-lasting softness and glow.
            </p>
          </div>
          <div className="border-t border-lum-border pt-8">
            <p className="overline text-lum-sale mb-3">✨ Vitamin C Glow Mask</p>
            <p className="font-serif-display text-2xl md:text-3xl leading-tight mb-4">Your instant glow-up.</p>
            <p className="text-sm text-lum-text2 leading-relaxed">
              Infused with Vitamin C, botanical extracts, and hyaluronic acid, this brightening mask helps even skin tone, reduce dullness, calm redness, and reveal a fresh, luminous complexion.
            </p>
          </div>
        </div>
      </section>

      {/* Why customers love it */}
      <section className="bg-lum-bg2 border-y border-lum-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="overline mb-3">🌿 Why customers love it</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">Luxury skincare<br/>results without<br/>the salon price.</h2>
          </div>
          <ul className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6 text-base">
            {[
              'Visible glow after just one use',
              'Intensive hydration & skin barrier support',
              'Smooths, plumps & refreshes tired skin',
              'Suitable for all skin types',
              'Spa-level results at home in 15 minutes',
            ].map((b) => (
              <li key={b} className="flex gap-3">
                <span className="text-lum-sale font-serif-display text-xl leading-none">✦</span>
                <span className="text-lum-text2 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Bundle section */}
      <section className="bg-lum-bg2">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 relative">
            <div className="aspect-[5/4] bg-lum-bg3 overflow-hidden">
              <img src="https://images.pexels.com/photos/6167446/pexels-photo-6167446.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Bundle flatlay" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-10 right-6 md:right-10 bg-lum-bg p-8 md:p-10 max-w-[320px] shadow-xl border border-lum-border">
              <p className="overline text-lum-sale">Best value</p>
              <h3 className="font-serif-display text-3xl mt-2 leading-tight">Buy 2 packs.<br/>Save 10%, automatically.</h3>
              <p className="text-sm text-lum-text2 mt-3">Stack any two formulas. Discount applies in cart.</p>
              <Link to="/shop" data-testid="bundle-shop-button" className="btn-primary mt-6 w-full">Build my bundle</Link>
            </div>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="overline mb-3">The 3-Mask Method</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">A different mask for every mood.</h2>
            <ul className="mt-8 space-y-6">
              {PRODUCTS.map((p) => (
                <li key={p.id} className="flex items-center gap-5">
                  <span className={`w-3 h-3 ${accentBg(p.accent)}`} />
                  <div>
                    <p className="font-serif-display text-2xl leading-none">{p.name.split(' ')[0]}</p>
                    <p className="text-sm text-lum-text2 mt-1">{p.tagline}</p>
                  </div>
                  <span className="ml-auto text-sm text-lum-muted">{format(p.pack_price)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Skin Quiz teaser */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <p className="overline mb-3">Take the 60-second quiz</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">Find your perfect ritual.</h2>
          <p className="mt-5 text-lum-text2 max-w-md">Answer four quick questions. We&apos;ll match the exact mask your skin is asking for, with no guesswork.</p>
          <Link to="/quiz" data-testid="home-quiz-cta" className="btn-primary mt-8 inline-flex">Start the quiz</Link>
        </div>
        <div className="md:col-span-7">
          <div className="aspect-[5/4] bg-lum-bg2 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1581182815808-b6eb627a8798?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Close portrait" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Countdown */}
      <CountdownBanner />

      {/* Testimonials */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <p className="overline mb-3">Read by women</p>
        <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] mb-16">The Lumière Diaries.</h2>
        <div className="space-y-16">
          {testimonials.map((t, i) => (
            <figure key={i} className="max-w-3xl mx-auto animate-fade-up" style={{ animationDelay: `${i * 100}ms` }}>
              <blockquote className="font-serif-display text-3xl md:text-4xl leading-[1.2] text-lum-text">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 text-sm tracking-[0.18em] uppercase text-lum-text2">— {t.author}, {t.age}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Closing CTA — Your Skin, Upgraded */}
      <section className="bg-lum-text text-lum-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <p className="overline mb-6 text-lum-bg/70">💖 Your skin, upgraded</p>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[1.05]">Turn your skincare<br/>into a self-care ritual.</h2>
          <p className="mt-6 text-lum-bg/70 max-w-xl mx-auto text-lg">Hydrate. Brighten. Transform — in just 15 minutes.</p>
          <p className="mt-3 text-lum-bg/50 max-w-xl mx-auto text-sm">Free gift in every order. Free shipping over $24.</p>
          <Link to="/shop" data-testid="closing-cta-shop" className="mt-10 inline-flex bg-lum-bg text-lum-text px-10 py-4 text-xs uppercase tracking-[0.22em]">Shop the masks</Link>
        </div>
      </section>
    </div>
  );
}
